import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Printer, Download, Mail, Calendar, Droplets } from "lucide-react";
import { useState, useEffect as React_useEffect } from "react";
import * as React from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import html2pdf from "html2pdf.js";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface PeriodLog {
  id: string;
  log_date: string;
  flow_intensity: string;
  notes: string | null;
}

interface DoctorReportProps {
  symptomEpisodes?: Record<string, Array<{ id?: string; symptomType: string; intensity: number; time: string; date?: string }>>;
  hotFlashTimes?: Array<{ time: string; date: string }>;
  bladderDiaryData?: Array<{
    date: string;
    intakeEntries: Array<{ time: string; amount: number; type: string }>;
    urinationEntries: Array<{ time: string; urgency: number; hadLeakage: boolean; leakageAmount?: string; notes?: string }>;
  }>;
  periodLogs?: PeriodLog[];
  dailyNotes?: Map<string, string>;
  dailyTriggers?: Map<string, string[]>;
  symptomTriggers?: Map<string, string[]>;
  selectedDate?: Date;
}

export const DoctorReport = ({ 
  symptomEpisodes: initialSymptomEpisodes = {}, 
  hotFlashTimes: initialHotFlashTimes = [], 
  bladderDiaryData: initialBladderDiaryData = [], 
  periodLogs: initialPeriodLogs = [],
  dailyNotes: initialDailyNotes = new Map(), 
  dailyTriggers: initialDailyTriggers = new Map(), 
  symptomTriggers = new Map(), 
  selectedDate = new Date()
}: DoctorReportProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [doctorEmail, setDoctorEmail] = useState("");
  const [patientName, setPatientName] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [startDate, setStartDate] = useState<Date>(selectedDate);
  const [endDate, setEndDate] = useState<Date>(selectedDate);
  const [loadingRange, setLoadingRange] = useState(false);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [doctorQuestions, setDoctorQuestions] = useState<string[]>([]);
  
  // State for range data
  const [symptomEpisodes, setSymptomEpisodes] = useState(initialSymptomEpisodes);
  const [hotFlashTimes, setHotFlashTimes] = useState(initialHotFlashTimes);
  const [bladderDiaryData, setBladderDiaryData] = useState(initialBladderDiaryData);
  const [periodLogs, setPeriodLogs] = useState<PeriodLog[]>(initialPeriodLogs);
  const [dailyNotes, setDailyNotes] = useState(initialDailyNotes);
  const [dailyTriggers, setDailyTriggers] = useState(initialDailyTriggers);

  // Generate questions when dialog opens
  React_useEffect(() => {
    if (isOpen && doctorQuestions.length === 0 && !generatingQuestions) {
      generateDoctorQuestions();
    }
  }, [isOpen]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const element = document.querySelector('.printable-report') as HTMLElement;
    if (!element) {
      toast.error("Unable to generate PDF. Please try again.");
      return;
    }

    toast.loading("Generating PDF...");

    const opt = {
      margin: 0.5,
      filename: `menopause-symptom-report-${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' as const }
    };

    try {
      await html2pdf().set(opt).from(element).save();
      toast.dismiss();
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to generate PDF. Please try again.");
      console.error("PDF generation error:", error);
    }
  };

  const handleSendEmail = async () => {
    if (!doctorEmail.trim() || !patientName.trim()) {
      toast.error("Please enter both patient name and doctor's email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(doctorEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSendingEmail(true);
    toast.loading("Sending email to doctor...");

    try {
      const reportData = generateReportData();
      
      const { data, error } = await supabase.functions.invoke('send-doctor-report', {
        body: {
          doctorEmail,
          patientName,
          reportData,
          bladderDiaryData,
          dailyNotes: Array.from(dailyNotes.entries()),
          dailyTriggers: Array.from(dailyTriggers.entries())
        }
      });

      if (error) throw error;

      toast.dismiss();
      toast.success(`Report sent successfully to ${doctorEmail}!`);
      setShowEmailDialog(false);
      setDoctorEmail("");
      setPatientName("");
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast.dismiss();
      toast.error(error.message || "Failed to send email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDateRangeApply = async () => {
    if (startDate > endDate) {
      toast.error("Start date must be before end date");
      return;
    }
    
    setLoadingRange(true);
    toast.loading("Loading data for date range...");
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.dismiss();
        toast.error("Please log in to load data");
        return;
      }

      // Fetch symptom logs for date range
      const { data: symptomData, error: symptomError } = await supabase
        .from('symptom_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', format(startDate, 'yyyy-MM-dd'))
        .lte('log_date', format(endDate, 'yyyy-MM-dd'))
        .order('log_date', { ascending: true });

      if (symptomError) throw symptomError;

      // Group symptoms by type with date info
      const groupedSymptoms: Record<string, Array<{ id?: string; symptomType: string; intensity: number; time: string; date: string }>> = {};
      symptomData?.forEach(log => {
        if (!groupedSymptoms[log.symptom_type]) {
          groupedSymptoms[log.symptom_type] = [];
        }
        groupedSymptoms[log.symptom_type].push({
          id: log.id,
          symptomType: log.symptom_type,
          intensity: log.intensity,
          time: new Date(log.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          date: log.log_date
        });
      });

      setSymptomEpisodes(groupedSymptoms);

      // Fetch hot flash times for date range
      const { data: hotFlashData, error: hotFlashError } = await supabase
        .from('hot_flash_times')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', format(startDate, 'yyyy-MM-dd'))
        .lte('log_date', format(endDate, 'yyyy-MM-dd'))
        .order('log_date', { ascending: true });

      if (hotFlashError) throw hotFlashError;

      const formattedHotFlashes = hotFlashData?.map(hf => ({
        time: hf.time_of_day,
        date: hf.log_date
      })) || [];

      setHotFlashTimes(formattedHotFlashes);

      // Fetch daily triggers for date range
      const { data: triggerData, error: triggerError } = await supabase
        .from('daily_triggers')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', format(startDate, 'yyyy-MM-dd'))
        .lte('log_date', format(endDate, 'yyyy-MM-dd'))
        .order('log_date', { ascending: true });

      if (triggerError) throw triggerError;

      const triggersMap = new Map<string, string[]>();
      triggerData?.forEach(trigger => {
        if (!triggersMap.has(trigger.log_date)) {
          triggersMap.set(trigger.log_date, []);
        }
        triggersMap.get(trigger.log_date)!.push(trigger.trigger_type);
      });

      setDailyTriggers(triggersMap);

      // Fetch daily notes for date range
      const { data: notesData, error: notesError } = await supabase
        .from('daily_notes')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', format(startDate, 'yyyy-MM-dd'))
        .lte('log_date', format(endDate, 'yyyy-MM-dd'))
        .order('log_date', { ascending: true });

      if (notesError) throw notesError;

      const notesMap = new Map<string, string>();
      notesData?.forEach(note => {
        if (note.notes) {
          notesMap.set(note.log_date, note.notes);
        }
      });

      setDailyNotes(notesMap);

      // Fetch bladder diary data for date range
      const { data: bladderData, error: bladderError } = await supabase
        .from('bladder_diary')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', format(startDate, 'yyyy-MM-dd'))
        .lte('log_date', format(endDate, 'yyyy-MM-dd'))
        .order('log_date', { ascending: true });

      if (bladderError) throw bladderError;

      const groupedBladderData: Record<string, any> = {};
      bladderData?.forEach(entry => {
        if (!groupedBladderData[entry.log_date]) {
          groupedBladderData[entry.log_date] = {
            intakeEntries: [],
            urinationEntries: []
          };
        }
        if (entry.entry_type === 'intake') {
          groupedBladderData[entry.log_date].intakeEntries.push({
            time: entry.log_time,
            amount: entry.fluid_amount_ml || 0,
            type: entry.fluid_type || 'water'
          });
        } else {
          groupedBladderData[entry.log_date].urinationEntries.push({
            time: entry.log_time,
            urgency: entry.urgency_level || 0,
            hadLeakage: entry.had_leakage || false,
            leakageAmount: entry.leakage_amount || undefined,
            notes: entry.notes || undefined
          });
        }
      });

      const formattedBladderData = Object.entries(groupedBladderData).map(([date, entries]) => ({
        date,
        ...entries
      }));

      setBladderDiaryData(formattedBladderData);

      // Fetch period logs for date range
      const { data: periodData, error: periodError } = await supabase
        .from('period_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', format(startDate, 'yyyy-MM-dd'))
        .lte('log_date', format(endDate, 'yyyy-MM-dd'))
        .order('log_date', { ascending: true });

      if (periodError) throw periodError;
      setPeriodLogs(periodData || []);

      toast.dismiss();
      toast.success("Date range data loaded successfully");
      
      // Generate AI questions after data is loaded
      await generateDoctorQuestions();
    } catch (error: any) {
      console.error("Error loading date range data:", error);
      toast.dismiss();
      toast.error("Failed to load data for date range");
    } finally {
      setLoadingRange(false);
    }
  };

  const generateDoctorQuestions = async () => {
    setGeneratingQuestions(true);
    try {
      const reportData = generateReportData();
      
      // Collect all unique triggers
      const allTriggers = Array.from(dailyTriggers.values()).flat();
      const uniqueTriggers = [...new Set(allTriggers)];

      // Calculate bladder data summary
      const bladderSummary = bladderDiaryData.reduce((acc, day) => {
        const totalIntake = day.intakeEntries.reduce((sum, entry) => sum + entry.amount, 0);
        const leakages = day.urinationEntries.filter(e => e.hadLeakage).length;
        const highUrgency = day.urinationEntries.filter(e => e.urgency >= 4).length;
        const urgencyLevels = day.urinationEntries.map(e => e.urgency).filter(u => u > 0);
        
        return {
          totalIntake: acc.totalIntake + totalIntake,
          bathroomTrips: acc.bathroomTrips + day.urinationEntries.length,
          leakageCount: acc.leakageCount + leakages,
          highUrgencyCount: acc.highUrgencyCount + highUrgency,
          urgencySum: acc.urgencySum + urgencyLevels.reduce((sum, u) => sum + u, 0),
          urgencyCount: acc.urgencyCount + urgencyLevels.length
        };
      }, { totalIntake: 0, bathroomTrips: 0, leakageCount: 0, highUrgencyCount: 0, urgencySum: 0, urgencyCount: 0 });

      const bladderData = bladderSummary.bathroomTrips > 0 ? {
        totalIntake: bladderSummary.totalIntake,
        bathroomTrips: bladderSummary.bathroomTrips,
        leakageCount: bladderSummary.leakageCount,
        highUrgencyCount: bladderSummary.highUrgencyCount,
        averageUrgency: bladderSummary.urgencyCount > 0 ? bladderSummary.urgencySum / bladderSummary.urgencyCount : 0
      } : undefined;

      const { data, error } = await supabase.functions.invoke('generate-doctor-questions', {
        body: {
          symptoms: reportData.symptoms,
          hotFlashCount: reportData.hotFlashCount,
          triggers: uniqueTriggers,
          bladderData,
          dateRange: reportData.dateRange,
          isMultiDay: reportData.isMultiDay
        }
      });

      if (error) throw error;
      
      if (data?.questions) {
        setDoctorQuestions(data.questions);
      }
    } catch (error: any) {
      console.error("Error generating questions:", error);
      // Fallback to generic questions if AI fails
      setDoctorQuestions([
        "Based on my symptom patterns, am I a candidate for hormone replacement therapy?",
        "What can I do about the symptoms affecting my daily life?",
        "Are there non-hormonal options you'd recommend for my symptom profile?",
        "Should I be concerned about any of these symptoms?"
      ]);
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const generateReportData = () => {
    const daysDifference = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const isMultiDay = daysDifference > 1;

    // Map symptom IDs to readable names
    const symptomNames: Record<string, string> = {
      hotflash: "Hot Flashes",
      sleep: "Sleep Quality",
      brainfog: "Brain Fog",
      mood: "Mood Changes",
      energy: "Energy Level",
      hydration: "Hydration"
    };

    // Process symptom episodes
    const symptomSummary = Object.entries(symptomEpisodes).map(([symptomId, episodes]) => {
      if (episodes.length === 0) return null;

      const intensities = episodes.map(e => e.intensity);
      const avgIntensity = (intensities.reduce((sum, val) => sum + val, 0) / intensities.length).toFixed(1);
      const maxIntensity = Math.max(...intensities);
      const minIntensity = Math.min(...intensities);

      // Group episodes by date for multi-day reports
      const episodesByDate = isMultiDay 
        ? episodes.reduce((acc, episode) => {
            const date = episode.date || format(selectedDate, 'yyyy-MM-dd');
            if (!acc[date]) acc[date] = [];
            acc[date].push(episode);
            return acc;
          }, {} as Record<string, typeof episodes>)
        : null;

      return {
        name: symptomNames[symptomId] || symptomId,
        frequency: `${episodes.length} ${episodes.length === 1 ? 'episode' : 'episodes'} recorded`,
        dailyAverage: isMultiDay ? `${(episodes.length / daysDifference).toFixed(1)} episodes/day` : null,
        averageIntensity: `${avgIntensity}/10`,
        peakIntensity: `${maxIntensity}/10`,
        lowestIntensity: `${minIntensity}/10`,
        episodes: episodes.map(e => ({
          id: e.id,
          time: e.time,
          date: e.date,
          intensity: e.intensity
        })),
        episodesByDate,
        notes: episodes.length > 1 
          ? `${episodes.length} episodes recorded${isMultiDay ? ` over ${daysDifference} days` : ''}. Peak intensity: ${maxIntensity}/10.`
          : `Single episode recorded at ${episodes[0].time} with intensity ${episodes[0].intensity}/10.`
      };
    }).filter(Boolean);

    // Aggregate hot flash times
    const hotFlashCount = hotFlashTimes.length;
    const hotFlashByTimeOfDay = hotFlashTimes.reduce((acc, hf) => {
      const hour = parseInt(hf.time.split(':')[0]);
      const timeOfDay = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : hour < 21 ? 'Evening' : 'Night';
      acc[timeOfDay] = (acc[timeOfDay] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Aggregate triggers across all days
    const allTriggers = Array.from(dailyTriggers.values()).flat();
    const triggerFrequency = allTriggers.reduce((acc, trigger) => {
      acc[trigger] = (acc[trigger] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Process period logs
    const periodSummary = periodLogs.length > 0 ? {
      totalDays: periodLogs.length,
      flowBreakdown: periodLogs.reduce((acc, log) => {
        acc[log.flow_intensity] = (acc[log.flow_intensity] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      dates: periodLogs.map(log => ({
        date: log.log_date,
        flow: log.flow_intensity,
        notes: log.notes
      }))
    } : null;

    return {
      patientName: "[Patient Name]",
      dateRange: isMultiDay 
        ? `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`
        : format(startDate, 'MMMM d, yyyy'),
      dayCount: daysDifference,
      isMultiDay,
      generatedDate: new Date().toLocaleDateString(),
      symptoms: symptomSummary,
      hotFlashTimes: hotFlashTimes,
      hotFlashCount,
      hotFlashByTimeOfDay,
      triggerFrequency,
      periodSummary,
      totalEpisodes: Object.values(symptomEpisodes).reduce((sum, eps) => sum + eps.length, 0)
    };
  };

  const reportData = generateReportData();

  return (
    <>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .printable-report, .printable-report * {
              visibility: visible;
            }
            .printable-report {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
            }
            .no-print {
              display: none !important;
            }
            .print-page-break {
              page-break-after: always;
            }
          }
        `}
      </style>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button size="sm" className="gap-2">
            <FileText className="h-4 w-4" />
            Doctor Report
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle>Doctor-Ready Symptom Report</DialogTitle>
          </DialogHeader>
          
          <div className="no-print flex flex-col gap-4 mb-4">
            {/* Date Range Selector */}
            <Card className="p-4 bg-muted/30">
              <Label className="text-sm font-semibold mb-3 block">Report Date Range</Label>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "MMM d, yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => date && setStartDate(date)}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "MMM d, yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={endDate}
                        onSelect={(date) => date && setEndDate(date)}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button onClick={handleDateRangeApply} size="sm" disabled={loadingRange}>
                  {loadingRange ? "Loading..." : "Apply Range"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Select a date range to generate a comprehensive multi-day report
              </p>
            </Card>
            <div className="flex gap-2">
              <Button onClick={handlePrint} variant="outline" className="flex-1">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button onClick={handleDownloadPDF} variant="outline" className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button onClick={() => setShowEmailDialog(true)} className="flex-1">
                <Mail className="h-4 w-4 mr-2" />
                Email
              </Button>
            </div>

            {/* Email Dialog */}
            {showEmailDialog && (
              <Card className="p-4 bg-muted/50">
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="patientName" className="text-sm font-medium">Patient Name</Label>
                    <Input
                      id="patientName"
                      type="text"
                      placeholder="Enter patient name"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="doctorEmail" className="text-sm font-medium">Doctor's Email</Label>
                    <Input
                      id="doctorEmail"
                      type="email"
                      placeholder="doctor@example.com"
                      value={doctorEmail}
                      onChange={(e) => setDoctorEmail(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSendEmail} 
                      disabled={sendingEmail}
                      className="flex-1"
                    >
                      {sendingEmail ? "Sending..." : "Send Email"}
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowEmailDialog(false);
                        setDoctorEmail("");
                        setPatientName("");
                      }} 
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Printable Report Content */}
          <div className="printable-report space-y-6">
            {/* Header */}
            <div className="text-center border-b-2 pb-4">
              <h1 className="text-2xl font-bold text-foreground">
                Menopause Symptom Tracking Report
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                {reportData.isMultiDay ? `${reportData.dayCount}-Day Comprehensive Report` : 'Daily Summary Report'}
              </p>
            </div>

            {/* Patient Info */}
            <Card className="p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-semibold text-foreground">Patient Name:</p>
                  <p className="text-muted-foreground">{reportData.patientName}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">{reportData.isMultiDay ? 'Report Period:' : 'Report Date:'}</p>
                  <p className="text-muted-foreground">{reportData.dateRange}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">Report Generated:</p>
                  <p className="text-muted-foreground">{reportData.generatedDate}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">Tracking Method:</p>
                  <p className="text-muted-foreground">Digital Daily Self-Report</p>
                </div>
              </div>
            </Card>

            {/* Hot Flash Analysis */}
            {reportData.hotFlashCount > 0 && (
              <>
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-foreground">Hot Flash Analysis</h2>
                  <Card className="p-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Total Episodes</p>
                        <p className="text-2xl font-bold text-primary">{reportData.hotFlashCount}</p>
                        {reportData.isMultiDay && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Avg: {(reportData.hotFlashCount / reportData.dayCount).toFixed(1)}/day
                          </p>
                        )}
                      </div>
                      <div className="p-3 bg-accent/10 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Peak Time</p>
                        <p className="text-lg font-bold text-accent">
                          {Object.entries(reportData.hotFlashByTimeOfDay).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {Object.entries(reportData.hotFlashByTimeOfDay).sort((a, b) => b[1] - a[1])[0]?.[1] || 0} episodes
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">Pattern by time of day:</p>
                    <div className="space-y-2">
                      {Object.entries(reportData.hotFlashByTimeOfDay).map(([timeOfDay, count]) => (
                        <div key={timeOfDay} className="flex items-center justify-between">
                          <span className="text-sm">{timeOfDay}</span>
                          <div className="flex items-center gap-2">
                            <div className="h-2 bg-accent/20 rounded-full w-32">
                              <div 
                                className="h-2 bg-accent rounded-full"
                                style={{ width: `${(count / reportData.hotFlashCount) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
                <Separator />
              </>
            )}

            {/* Period Tracking */}
            {reportData.periodSummary && reportData.periodSummary.totalDays > 0 && (
              <>
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-foreground flex items-center gap-2">
                    <Droplets className="h-5 w-5 text-primary/80" /> Menstrual Cycle Report
                  </h2>
                  <Card className="p-4 bg-pink-50 dark:bg-pink-950/20">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-background/80 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Period Days</p>
                        <p className="text-2xl font-bold text-pink-600">{reportData.periodSummary.totalDays}</p>
                      </div>
                      {Object.entries(reportData.periodSummary.flowBreakdown).map(([flow, count]) => (
                        <div key={flow} className="p-3 bg-background/80 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1 capitalize">{flow}</p>
                          <p className="text-lg font-bold text-pink-500">{count as number} days</p>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-sm font-medium text-foreground mb-2">Daily Log:</p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {reportData.periodSummary.dates.map((entry: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between text-sm p-2 bg-background/60 rounded">
                          <span className="text-muted-foreground">
                            {format(new Date(entry.date), 'MMM d, yyyy')}
                          </span>
                          <span className="font-medium capitalize text-pink-600">{entry.flow}</span>
                          {entry.notes && (
                            <span className="text-xs text-muted-foreground truncate max-w-32">{entry.notes}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
                <Separator />
              </>
            )}

            {/* Episode Summary */}
            {reportData.totalEpisodes > 0 && (
              <>
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-foreground">Episode Summary</h2>
                  <Card className="p-4 bg-primary/5">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-foreground">Total Episodes Recorded:</p>
                        <p className="text-2xl font-bold text-primary">{reportData.totalEpisodes}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">Symptoms Tracked:</p>
                        <p className="text-2xl font-bold text-primary">{reportData.symptoms.length}</p>
                      </div>
                    </div>
                  </Card>
                </div>
                <Separator />
              </>
            )}

            {/* Symptom Details */}
            {reportData.symptoms.length > 0 ? (
              <>
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-foreground">Detailed Symptom Report</h2>
                  <div className="space-y-4">
                    {reportData.symptoms.map((symptom: any, index: number) => (
                      <Card key={index} className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-lg text-foreground">{symptom.name}</h3>
                          <span className="text-sm font-medium text-muted-foreground">
                            Avg: {symptom.averageIntensity}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-4 text-sm mb-3">
                          <div>
                            <span className="font-medium text-foreground">Episodes: </span>
                            <span className="text-muted-foreground">{symptom.frequency}</span>
                          </div>
                          {symptom.dailyAverage && (
                            <div>
                              <span className="font-medium text-foreground">Daily Avg: </span>
                              <span className="text-muted-foreground">{symptom.dailyAverage}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-medium text-foreground">Peak: </span>
                            <span className="text-muted-foreground">{symptom.peakIntensity}</span>
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Lowest: </span>
                            <span className="text-muted-foreground">{symptom.lowestIntensity}</span>
                          </div>
                        </div>

                        {/* Episode Timeline */}
                        <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                          <p className="text-xs font-semibold text-foreground mb-2">Episode Timeline:</p>
                          <div className="space-y-1.5">
                            {symptom.episodes.map((episode: any, epIndex: number) => (
                              <div key={epIndex} className="space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">
                                    {reportData.isMultiDay && episode.date && (
                                      <span className="font-medium mr-2">
                                        {format(new Date(episode.date), 'MMM d')}:
                                      </span>
                                    )}
                                    {episode.time}
                                  </span>
                                  <span className="font-medium text-foreground">
                                    Intensity: {episode.intensity}/10
                                    <span className={cn(
                                      "ml-2 px-2 py-0.5 rounded text-[10px] font-medium",
                                      episode.intensity >= 7 ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" :
                                      episode.intensity >= 4 ? "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300" :
                                      "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300"
                                    )}>
                                      {episode.intensity >= 7 ? 'Severe' : episode.intensity >= 4 ? 'Moderate' : 'Mild'}
                                    </span>
                                  </span>
                                </div>
                                {episode.id && symptomTriggers.has(episode.id) && symptomTriggers.get(episode.id)!.length > 0 && (
                                  <div className="ml-4 flex flex-wrap gap-1 mt-1">
                                    <span className="text-[10px] text-muted-foreground">Triggers:</span>
                                    {symptomTriggers.get(episode.id)!.map((trigger, tIndex) => {
                                      const displayName = trigger.startsWith('custom_') 
                                        ? trigger.replace('custom_', '').replace(/_/g, ' ').charAt(0).toUpperCase() + trigger.replace('custom_', '').replace(/_/g, ' ').slice(1)
                                        : trigger.replace(/_/g, ' ').charAt(0).toUpperCase() + trigger.replace(/_/g, ' ').slice(1);
                                      return (
                                        <span 
                                          key={tIndex}
                                          className="px-1.5 py-0.5 bg-accent/20 text-accent rounded text-[10px]"
                                        >
                                          {displayName}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="text-sm mt-3 pt-3 border-t">
                          <span className="font-medium text-foreground">Clinical Notes: </span>
                          <span className="text-muted-foreground">{symptom.notes}</span>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
                <Separator />
              </>
            ) : (
              <div>
                <Card className="p-6 text-center">
                  <p className="text-muted-foreground">No symptom episodes recorded for this date.</p>
                  <p className="text-sm text-muted-foreground mt-2">Start tracking symptoms to generate a comprehensive report for your healthcare provider.</p>
                </Card>
                <Separator />
              </div>
            )}

            {/* Bladder Diary Section */}
            {bladderDiaryData && bladderDiaryData.length > 0 && (
              <>
                <Separator className="print-page-break" />
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-foreground">Bladder Diary Summary</h2>
                  <Card className="p-4">
                    <p className="text-sm text-muted-foreground mb-4">
                      Tracking fluid intake and urination patterns helps identify bladder health concerns and potential triggers.
                    </p>
                    
                    <div className="space-y-4">
                      {bladderDiaryData.slice(-14).map((dayData, index) => (
                        <div key={index} className="border-b pb-3 last:border-0">
                          <h3 className="font-semibold text-foreground mb-2">{dayData.date}</h3>
                          
                          {/* Intake Summary */}
                          {dayData.intakeEntries.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-foreground mb-1">Fluid Intake:</p>
                              <div className="flex flex-wrap gap-2">
                                {dayData.intakeEntries.map((entry, idx) => (
                                  <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                    {entry.time} - {entry.amount}ml {entry.type}
                                  </span>
                                ))}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Total: {dayData.intakeEntries.reduce((sum, e) => sum + e.amount, 0)}ml
                              </p>
                            </div>
                          )}
                          
                          {/* Urination Summary */}
                          {dayData.urinationEntries.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-foreground mb-1">Urination Events ({dayData.urinationEntries.length}):</p>
                              <div className="flex flex-wrap gap-2">
                                {dayData.urinationEntries.map((entry, idx) => (
                                  <span 
                                    key={idx} 
                                    className={`text-xs px-2 py-1 rounded ${
                                      entry.hadLeakage 
                                        ? 'bg-red-100 text-red-800' 
                                        : entry.urgency >= 4 
                                        ? 'bg-orange-100 text-orange-800' 
                                        : 'bg-green-100 text-green-800'
                                    }`}
                                  >
                                    {entry.time} (U:{entry.urgency}/5)
                                    {entry.hadLeakage && ` - ${entry.leakageAmount} leakage`}
                                  </span>
                                ))}
                              </div>
                              {dayData.urinationEntries.some(e => e.hadLeakage) && (
                                <p className="text-xs text-red-600 mt-1 font-medium">
                                  Leakage reported: {dayData.urinationEntries.filter(e => e.hadLeakage).length} episode(s)
                                </p>
                              )}
                              {dayData.urinationEntries.some(e => e.notes) && (
                                <div className="mt-1">
                                  <p className="text-xs font-medium text-foreground">Notes:</p>
                                  {dayData.urinationEntries
                                    .filter(e => e.notes)
                                    .map((entry, idx) => (
                                      <p key={idx} className="text-xs text-muted-foreground italic">
                                        • {entry.notes}
                                      </p>
                                    ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 p-3 bg-primary-soft/30 rounded text-xs">
                      <p className="font-semibold text-foreground mb-1">Clinical Notes:</p>
                      <p className="text-foreground flex items-center flex-wrap gap-y-2">
                        <span>Urgency Scale: 1 (no urgency) to 5 (extreme urgency)</span>
                        <span className="mx-2">|</span>
                        <span className="flex items-center gap-1.5">
                          <span className="inline-block w-4 h-4 bg-green-500 rounded"></span>
                          <span>Normal</span>
                        </span>
                        <span className="flex items-center gap-1.5 ml-3">
                          <span className="inline-block w-4 h-4 bg-orange-500 rounded"></span>
                          <span>High Urgency</span>
                        </span>
                        <span className="flex items-center gap-1.5 ml-3">
                          <span className="inline-block w-4 h-4 bg-red-600 rounded"></span>
                          <span>Leakage</span>
                        </span>
                      </p>
                    </div>
                  </Card>
                </div>
              </>
            )}

            {/* Patient Notes */}
            {dailyNotes.size > 0 && (
              <>
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-foreground">Patient Notes</h2>
                  {reportData.isMultiDay ? (
                    <div className="space-y-3">
                      {Array.from(dailyNotes.entries()).map(([noteDate, note]) => (
                        <Card key={noteDate} className="p-4">
                          <p className="text-xs font-semibold text-muted-foreground mb-2">
                            {format(new Date(noteDate), 'MMMM d, yyyy')}
                          </p>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{note}</p>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="p-4">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {Array.from(dailyNotes.values())[0]}
                      </p>
                    </Card>
                  )}
                </div>
                <Separator />
              </>
            )}

            {/* Potential Triggers */}
            {Object.keys(reportData.triggerFrequency).length > 0 && (
              <>
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-foreground">Potential Triggers</h2>
                  <Card className="p-4">
                    <p className="text-sm text-muted-foreground mb-3">
                      {reportData.isMultiDay 
                        ? `Factors that may have influenced symptoms during this period (showing frequency):`
                        : `Factors that may have influenced symptoms on this date:`
                      }
                    </p>
                    <div className="space-y-2">
                      {Object.entries(reportData.triggerFrequency)
                        .sort((a, b) => b[1] - a[1])
                        .map(([trigger, count]) => {
                          const displayName = trigger.startsWith('custom_') 
                            ? trigger.replace('custom_', '').replace(/_/g, ' ').charAt(0).toUpperCase() + trigger.replace('custom_', '').replace(/_/g, ' ').slice(1)
                            : trigger.replace(/_/g, ' ').charAt(0).toUpperCase() + trigger.replace(/_/g, ' ').slice(1);
                          return (
                            <div key={trigger} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                              <span className="text-sm font-medium">{displayName}</span>
                              {reportData.isMultiDay && (
                                <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                                  {count} {count === 1 ? 'day' : 'days'}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </Card>
                </div>
                <Separator />
              </>
            )}

            {/* Questions for Doctor */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-foreground">Questions for Healthcare Provider</h2>
                <Button 
                  onClick={generateDoctorQuestions} 
                  disabled={generatingQuestions}
                  variant="outline"
                  size="sm"
                  className="no-print"
                >
                  {generatingQuestions ? "Generating..." : "Regenerate Questions"}
                </Button>
              </div>
              <Card className="p-4">
                {generatingQuestions ? (
                  <div className="space-y-3 animate-pulse">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="h-4 bg-muted rounded"></div>
                    ))}
                  </div>
                ) : doctorQuestions.length > 0 ? (
                  <div className="space-y-3 text-sm text-muted-foreground">
                    {doctorQuestions.map((question, index) => (
                      <p key={index}>{index + 1}. {question}</p>
                    ))}
                    <div className="border-t pt-3 mt-4">
                      <p className="font-medium text-foreground">Additional Notes/Concerns:</p>
                      <div className="h-20 border-b-2 mt-2"></div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <p>1. Based on my symptom patterns, am I a candidate for hormone replacement therapy?</p>
                    <p>2. What can I do about the correlation between symptoms affecting my daily life?</p>
                    <p>3. Are there non-hormonal options you'd recommend for my symptom profile?</p>
                    <p>4. Should I be concerned about any of these symptoms?</p>
                    <div className="border-t pt-3 mt-4">
                      <p className="font-medium text-foreground">Additional Notes/Concerns:</p>
                      <div className="h-20 border-b-2 mt-2"></div>
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-4 italic">
                  {doctorQuestions.length > 0 
                    ? "These questions are AI-generated based on your specific symptom patterns."
                    : "Click 'Regenerate Questions' to generate personalized questions based on your symptom data."}
                </p>
              </Card>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-muted-foreground pt-4 border-t">
              <p>This report is based on patient self-reported daily tracking data.</p>
              <p className="mt-1">Generated by MenopauseWise™ Symptom Tracker</p>
            </div>
          </div>

          <div className="no-print mt-6 p-4 bg-primary-soft rounded-lg">
            <p className="text-sm text-foreground">
              <strong>Tip:</strong> Click "Print Report" and select "Save as PDF" in your print dialog to save a digital copy for your doctor.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
